import * as React from 'react'
import { useTranslation } from 'react-i18next'

import { EmailEventType } from '#/shared/api/generated/communication/models'
import type { EmailEventRead } from '#/shared/api/generated/communication/models'
import { formatNoteTimestamp } from '#/shared/lib/format'
import { cn } from '#/shared/lib/utils'
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from '#/shared/ui/hover-card'
import { Skeleton } from '#/shared/ui/skeleton'

import { useEmailEvents } from '../../model/use-email-events'

const EVENT_LABEL_KEY: Record<EmailEventType, string> = {
  [EmailEventType.processed]:
    'orderDetails.history.email.events.type.processed',
  [EmailEventType.delivered]:
    'orderDetails.history.email.events.type.delivered',
  [EmailEventType.opened]: 'orderDetails.history.email.events.type.opened',
  [EmailEventType.clicked]: 'orderDetails.history.email.events.type.clicked',
  [EmailEventType.bounced]: 'orderDetails.history.email.events.type.bounced',
  [EmailEventType.dropped]: 'orderDetails.history.email.events.type.dropped',
  [EmailEventType.deferred]: 'orderDetails.history.email.events.type.deferred',
  [EmailEventType.spam_reported]:
    'orderDetails.history.email.events.type.spam_reported',
  [EmailEventType.unsubscribed]:
    'orderDetails.history.email.events.type.unsubscribed',
  [EmailEventType.resubscribed]:
    'orderDetails.history.email.events.type.resubscribed',
}

const NEGATIVE_EVENTS: ReadonlySet<EmailEventType> = new Set([
  EmailEventType.bounced,
  EmailEventType.dropped,
  EmailEventType.spam_reported,
])

function eventTone(type: EmailEventType): string {
  return NEGATIVE_EVENTS.has(type) ? 'text-red-500' : 'text-gray-700'
}

function EmailEventRow({ event }: { event: EmailEventRead }) {
  const { t } = useTranslation()
  const detail = event.url ?? event.reason
  return (
    <li className="flex flex-col gap-0.5">
      <div className="flex items-baseline justify-between gap-3">
        <span className={cn('text-xs font-medium', eventTone(event.type))}>
          {t(EVENT_LABEL_KEY[event.type])}
        </span>
        <span className="text-xs whitespace-nowrap text-gray-500">
          {formatNoteTimestamp(event.at)}
        </span>
      </div>
      {detail && (
        <span className="text-xs break-all text-gray-500">{detail}</span>
      )}
    </li>
  )
}

function EmailEventsList({
  emailId,
  active,
}: {
  emailId: string
  active: boolean
}) {
  const { t } = useTranslation()
  const { events, isLoading, isError } = useEmailEvents(emailId, active)
  if (isLoading) {
    return (
      <div className="flex flex-col gap-1.5">
        <Skeleton className="h-3.5 w-full" />
        <Skeleton className="h-3.5 w-2/3" />
      </div>
    )
  }
  if (isError) {
    return (
      <p className="text-xs text-gray-500">
        {t('orderDetails.history.email.events.error')}
      </p>
    )
  }
  if (events.length === 0) {
    return (
      <p className="text-xs text-gray-500">
        {t('orderDetails.history.email.events.empty')}
      </p>
    )
  }
  return (
    <ul className="flex flex-col gap-1.5">
      {events.map((event, index) => (
        <EmailEventRow
          key={`${event.type}-${event.at}-${index}`}
          event={event}
        />
      ))}
    </ul>
  )
}

/** Hover the delivery tick to see the email's SendGrid event trail. */
export function EmailEventsHoverCard({
  emailId,
  children,
}: {
  emailId: string
  children: React.ReactNode
}) {
  const [open, setOpen] = React.useState(false)
  return (
    <HoverCard openDelay={200} closeDelay={100} onOpenChange={setOpen}>
      <HoverCardTrigger asChild>
        <span className="inline-flex cursor-default">{children}</span>
      </HoverCardTrigger>
      <HoverCardContent align="end" className="w-60 p-3">
        <EmailEventsList emailId={emailId} active={open} />
      </HoverCardContent>
    </HoverCard>
  )
}
